import { useMemo, useState, type FormEvent } from 'react';
import type { TimesheetStatus } from '@dsr/shared-types';
import {
  useTimesheets,
  useCreateTimesheet,
  useUpdateTimesheet,
  useSubmitTimesheet,
  useApproveTimesheet,
  useExportTimesheet,
  useDeleteTimesheet,
  type Timesheet,
} from '../api/queries/timesheets';
import { usePersonnelList } from '../api/queries/personnel';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import { fmtDate } from '../utils/date';
import { firstFieldError } from './IncidentsPage';
import './reports.css';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<TimesheetStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  exported: 'Exported',
};

const STATUS_OPTIONS: TimesheetStatus[] = ['draft', 'submitted', 'approved', 'exported'];

// Map timesheet statuses onto the shared Badge tones so the page stays
// on-brand without new CSS (mirrors the old rpt-badge-* tones).
const STATUS_TONE: Record<TimesheetStatus, BadgeTone> = {
  draft: 'neutral',
  submitted: 'accent',
  approved: 'success',
  exported: 'info',
};

type View = 'all' | 'queue';

// ===========================================================================
// Timesheets page (list / filters + submit + approval queue)
// ===========================================================================

export function TimesheetsPage(): JSX.Element {
  const [view, setView] = useState<View>('all');
  const [statusFilter, setStatusFilter] = useState<TimesheetStatus | 'all'>('all');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Timesheet | null>(null);
  const [deleting, setDeleting] = useState<Timesheet | null>(null);

  const canApprove = can('timesheet.approve');

  // In the approval-queue view we force the status to 'submitted'; otherwise we
  // honour the status filter (or fetch all).
  const effectiveStatus: TimesheetStatus | 'all' =
    view === 'queue' ? 'submitted' : statusFilter;

  const { data, isPending, error } = useTimesheets(
    effectiveStatus !== 'all' ? { status: effectiveStatus } : {},
  );

  const personnelQuery = usePersonnelList();
  const personnel = personnelQuery.data?.personnel ?? [];
  const officerName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of personnel) m.set(p.id, p.legal_name);
    return m;
  }, [personnel]);

  const submitTimesheet = useSubmitTimesheet();
  const approveTimesheet = useApproveTimesheet();
  const exportTimesheet = useExportTimesheet();
  const deleteTimesheet = useDeleteTimesheet();

  const busyLifecycle =
    submitTimesheet.isPending ||
    approveTimesheet.isPending ||
    exportTimesheet.isPending;

  const runLifecycle = async (
    label: string,
    fn: () => Promise<unknown>,
  ): Promise<void> => {
    try {
      await fn();
      toast.success(label);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not update the timesheet.'));
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteTimesheet.mutateAsync(deleting.id);
      toast.success('Timesheet deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not delete the timesheet.'));
    }
  };

  if (isPending) return <p className="rpt-status-line">Loading timesheets…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view timesheets."
        : 'Failed to load timesheets.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const timesheets = data?.timesheets ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>Timesheets</h1>
          <p className="page-sub">
            {view === 'queue'
              ? 'Submitted timesheets awaiting approval.'
              : 'Pay-period hours by officer and their approval status.'}
          </p>
        </div>
        <div className="page-toolbar">
          <div className="rpt-inline-actions">
            <button
              type="button"
              className={`rpt-link${view === 'all' ? '' : ''}`}
              onClick={() => setView('all')}
              aria-pressed={view === 'all'}
              style={view === 'all' ? { color: 'var(--ink)' } : undefined}
            >
              All
            </button>
            {canApprove && (
              <button
                type="button"
                className="rpt-link"
                onClick={() => setView('queue')}
                aria-pressed={view === 'queue'}
                style={view === 'queue' ? { color: 'var(--ink)' } : undefined}
              >
                Approval queue
              </button>
            )}
          </div>
          {can('timesheet.write') && (
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New timesheet
            </Button>
          )}
        </div>
      </header>

      {view === 'all' && (
        <div className="filters-bar">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as TimesheetStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </div>
      )}

      {timesheets.length === 0 ? (
        <p className="rpt-empty">
          {view === 'queue'
            ? 'The approval queue is clear.'
            : 'No timesheets match these filters.'}
        </p>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Officer</Table.HeaderCell>
              <Table.HeaderCell>Pay period</Table.HeaderCell>
              <Table.HeaderCell>Regular</Table.HeaderCell>
              <Table.HeaderCell>OT</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {timesheets.map((ts) => (
              <Table.Row key={ts.id}>
                <Table.Cell tone="strong">
                  {officerName.get(ts.personnel_id) ?? `Officer #${ts.personnel_id}`}
                </Table.Cell>
                <Table.Cell>
                  {ts.period_start || ts.period_end
                    ? `${fmtDate(ts.period_start)} – ${fmtDate(ts.period_end)}`
                    : '—'}
                </Table.Cell>
                <Table.Cell tone="mono">{ts.regular_hours}</Table.Cell>
                <Table.Cell tone="mono">{ts.ot_hours}</Table.Cell>
                <Table.Cell>
                  <Badge tone={STATUS_TONE[ts.status]}>{STATUS_LABEL[ts.status]}</Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="rpt-inline-actions">
                    {can('timesheet.write') &&
                      (ts.status === 'draft' || ts.status === 'submitted') && (
                        <button
                          type="button"
                          className="rpt-link"
                          onClick={() => setEditing(ts)}
                        >
                          edit
                        </button>
                      )}
                    {can('timesheet.write') && ts.status === 'draft' && (
                      <button
                        type="button"
                        className="rpt-link"
                        disabled={busyLifecycle}
                        onClick={() =>
                          void runLifecycle('Timesheet submitted.', () =>
                            submitTimesheet.mutateAsync(ts.id),
                          )
                        }
                      >
                        submit
                      </button>
                    )}
                    {canApprove && ts.status === 'submitted' && (
                      <button
                        type="button"
                        className="rpt-link"
                        disabled={busyLifecycle}
                        onClick={() =>
                          void runLifecycle('Timesheet approved.', () =>
                            approveTimesheet.mutateAsync(ts.id),
                          )
                        }
                      >
                        approve
                      </button>
                    )}
                    {can('timesheet.export') && ts.status === 'approved' && (
                      <button
                        type="button"
                        className="rpt-link"
                        disabled={busyLifecycle}
                        onClick={() =>
                          void runLifecycle('Timesheet exported to payroll.', () =>
                            exportTimesheet.mutateAsync(ts.id),
                          )
                        }
                      >
                        export
                      </button>
                    )}
                    {can('timesheet.write') &&
                      (ts.status === 'draft' || ts.status === 'submitted') && (
                        <button
                          type="button"
                          className="rpt-link rpt-link-danger"
                          onClick={() => setDeleting(ts)}
                        >
                          delete
                        </button>
                      )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {showNew && (
        <TimesheetFormModal
          mode="create"
          personnel={personnel}
          onClose={() => setShowNew(false)}
        />
      )}
      {editing && (
        <TimesheetFormModal
          mode="edit"
          timesheet={editing}
          personnel={personnel}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete timesheet"
        message={
          deleting
            ? `Delete the ${STATUS_LABEL[deleting.status].toLowerCase()} timesheet for ${
                officerName.get(deleting.personnel_id) ?? `Officer #${deleting.personnel_id}`
              }? This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteTimesheet.isPending}
        onConfirm={() => void onDelete()}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}

// ===========================================================================
// Create / edit modal
// ===========================================================================

interface TimesheetFormModalProps {
  mode: 'create' | 'edit';
  timesheet?: Timesheet;
  personnel: { id: number; legal_name: string }[];
  onClose: () => void;
}

const HOURS_RE = /^\d+(\.\d{1,2})?$/;

function TimesheetFormModal({
  mode,
  timesheet,
  personnel,
  onClose,
}: TimesheetFormModalProps): JSX.Element {
  const createTimesheet = useCreateTimesheet();
  const updateTimesheet = useUpdateTimesheet();
  const busy = createTimesheet.isPending || updateTimesheet.isPending;

  const [personnelId, setPersonnelId] = useState<number | ''>(
    timesheet?.personnel_id ?? '',
  );
  const [periodStart, setPeriodStart] = useState<number | null>(
    timesheet?.period_start ?? null,
  );
  const [periodEnd, setPeriodEnd] = useState<number | null>(
    timesheet?.period_end ?? null,
  );
  const [regularHours, setRegularHours] = useState(timesheet?.regular_hours ?? '0');
  const [otHours, setOtHours] = useState(timesheet?.ot_hours ?? '0');
  const [notes, setNotes] = useState(timesheet?.notes ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    if (mode === 'create' && personnelId === '') {
      setFormError('An officer is required.');
      return;
    }
    if (!HOURS_RE.test(regularHours) || !HOURS_RE.test(otHours)) {
      setFormError('Hours must be a number with up to two decimals (e.g. 40 or 8.5).');
      return;
    }
    if (periodStart !== null && periodEnd !== null && periodEnd < periodStart) {
      setFormError('Period end must be on or after the period start.');
      return;
    }

    try {
      if (mode === 'create') {
        await createTimesheet.mutateAsync({
          personnel_id: personnelId as number,
          period_start: periodStart ?? undefined,
          period_end: periodEnd ?? undefined,
          regular_hours: regularHours,
          ot_hours: otHours,
          notes: notes.trim() || undefined,
        });
        toast.success('Timesheet created.');
      } else if (timesheet) {
        await updateTimesheet.mutateAsync({
          id: timesheet.id,
          period_start: periodStart,
          period_end: periodEnd,
          regular_hours: regularHours,
          ot_hours: otHours,
          notes: notes.trim() || null,
        });
        toast.success('Timesheet updated.');
      }
      onClose();
    } catch (err) {
      setFormError(firstFieldError(err, 'Could not save the timesheet.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New timesheet' : 'Edit timesheet'}
      open
      busy={busy}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="timesheet-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create timesheet' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="timesheet-form" className="rpt-form" onSubmit={onSubmit}>
        <FormField label={`Officer${mode === 'create' ? ' *' : ''}`}>
          <select
            className="form-input"
            value={personnelId === '' ? '' : String(personnelId)}
            onChange={(e) =>
              setPersonnelId(e.target.value === '' ? '' : Number(e.target.value))
            }
            // personnel_id is immutable after creation (it's not in the API
            // update schema), so lock the picker in edit mode.
            disabled={busy || mode === 'edit'}
          >
            <option value="">Select an officer…</option>
            {personnel.map((p) => (
              <option key={p.id} value={p.id}>
                {p.legal_name}
              </option>
            ))}
          </select>
        </FormField>

        <FormRow>
          <FormField label="Period start">
            <DateInput value={periodStart} onChange={setPeriodStart} disabled={busy} />
          </FormField>
          <FormField label="Period end">
            <DateInput value={periodEnd} onChange={setPeriodEnd} disabled={busy} />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Regular hours">
            <Input
              type="text"
              inputMode="decimal"
              value={regularHours}
              onChange={(e) => setRegularHours(e.target.value)}
              placeholder="40"
              disabled={busy}
            />
          </FormField>
          <FormField label="Overtime hours">
            <Input
              type="text"
              inputMode="decimal"
              value={otHours}
              onChange={(e) => setOtHours(e.target.value)}
              placeholder="0"
              disabled={busy}
            />
          </FormField>
        </FormRow>

        <FormField label="Notes">
          <Textarea
            className="rpt-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adjustments, missed punches, approver notes…"
            maxLength={10_000}
            disabled={busy}
          />
        </FormField>

        {formError && (
          <p className="rpt-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </Dialog>
  );
}
